import { FilterKeys } from '@schooly/api';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MarkbookViewSettings } from '../MarkbookSettings/MarkbookSettings';
import { MarkbookBaselineConfig } from './types';

type BaselineAssessmentConfigSettings = {
  settings: MarkbookViewSettings;
  setBenchmarkEnabled: (enabled: boolean) => void;
  benchmarkEnabled: boolean;
};

export const useBaselineAssessmentConfig = ({
  settings,
  setBenchmarkEnabled,
  benchmarkEnabled,
}: BaselineAssessmentConfigSettings) => {
  const [baselineAssessmentConfig, setBaselineAssessmentConfig] =
    useState<MarkbookBaselineConfig | null>(null);

  const prevSettings = useRef(settings);

  const handleSetBaselineAssessmentConfig = useCallback(
    (v: MarkbookBaselineConfig | null) => {
      if (v) {
        setBenchmarkEnabled(false);
      }
      setBaselineAssessmentConfig((prev) => {
        if (!v) return null;

        switch (v.kind) {
          case 'assessment':
            if (prev?.kind !== 'assessment') return v;
            if (
              v.assessmentId === prev?.assessmentId &&
              prev.method.id === v.method.id &&
              prev.subject.id === v.subject.id
            )
              return null;
            return v;
          case 'average':
            return v;
        }

        return null;
      });
    },
    [setBenchmarkEnabled],
  );

  const baselineAssessmentMethodMissing =
    baselineAssessmentConfig &&
    baselineAssessmentConfig.kind === 'assessment' &&
    !!settings.methodType.length
      ? !settings.methodType.includes(baselineAssessmentConfig.method.method_type)
      : false;

  const baselineConfigKind = baselineAssessmentConfig?.kind;

  useEffect(() => {
    const { coloredCells, columnArrangeBy, methodType } = prevSettings.current;
    const shouldResetBaseline =
      benchmarkEnabled ||
      (coloredCells !== settings.coloredCells && settings.coloredCells) ||
      columnArrangeBy !== settings.columnArrangeBy;

    switch (baselineConfigKind) {
      case 'assessment':
        if (shouldResetBaseline || baselineAssessmentMethodMissing) {
          setBaselineAssessmentConfig(null);
        }

        break;
      case 'average':
        const averageResultsDisabled = !settings.avgResults;
        const methodChanged =
          settings.methodType.slice().sort().join(',') !== methodType?.slice().sort().join(',');
        if (shouldResetBaseline || averageResultsDisabled || methodChanged) {
          setBaselineAssessmentConfig(null);
        }

        break;
    }

    prevSettings.current = settings;
  }, [settings, baselineAssessmentMethodMissing, baselineConfigKind, benchmarkEnabled]);

  if (settings.columnArrangeBy !== FilterKeys.Subject)
    return { baselineAssessmentConfig: null, setBaselineAssessmentConfig: undefined };

  return {
    baselineAssessmentConfig,
    setBaselineAssessmentConfig: handleSetBaselineAssessmentConfig,
  };
};
